/**
 * Cloud API Service Layer
 * 
 * Communicates with the Docker/Cloud backend (AWS replacement).
 * Handles authentication, user management, cloud projects, and deployments.
 */

import { getAuthToken, getStoredToken, setAuthToken, getRefreshToken, persistAuthTokens, clearAuthTokens } from '../utils/auth';

const CLOUD_API_BASE = process.env.REACT_APP_CLOUD_API_URL || 'http://localhost:8080';

export { setAuthToken };

function fetchWithTimeout(url, options = {}, timeoutMs = 30000) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(id));
}

let _refreshPromise = null;

/**
 * POST /api/auth/refresh with the stored refresh JWT.
 * Shared by cloudFetch, apiFetch, uploads, and taskStore (single-flight).
 */
export async function refreshAuthToken() {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    if (_refreshPromise) return _refreshPromise;
    _refreshPromise = (async () => {
        try {
            const response = await fetch(`${CLOUD_API_BASE}/api/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });
            if (response.ok) {
                const data = await response.json();
                if (data.access_token) {
                    persistAuthTokens({
                        accessToken: data.access_token,
                        refreshToken: data.refresh_token,
                    });
                    return true;
                }
            }
            clearAuthTokens();
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
            return false;
        } catch (err) {
            console.error('Token refresh failed:', err);
            clearAuthTokens();
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
            return false;
        } finally {
            _refreshPromise = null;
        }
    })();
    return _refreshPromise;
}

/**
 * Generic fetch wrapper with authentication
 */
async function cloudFetch(endpoint, options = {}, _isRetry = false) {
    const url = `${CLOUD_API_BASE}${endpoint}`;
    const token = getAuthToken();
    const method = (options.method || 'GET').toUpperCase();

    const headers = { ...options.headers };
    if (['POST', 'PUT', 'PATCH'].includes(method)) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    try {
        const response = await fetchWithTimeout(url, {
            ...options,
            headers,
        });

        if (response.status === 401) {
            if (!_isRetry) {
                const refreshed = await refreshAuthToken();
                if (refreshed) {
                    return cloudFetch(endpoint, options, true);
                }
            }
            clearAuthTokens();
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({ detail: response.statusText }));
            const detail = typeof error.detail === 'object' ? JSON.stringify(error.detail) : (error.detail || error.message || `API Error: ${response.status}`);
            const err = new Error(detail);
            err.status = response.status;
            throw err;
        }

        const text = await response.text();
        return text ? JSON.parse(text) : {};
    } catch (error) {
        console.error(`Cloud API Error [${endpoint}]:`, error);
        throw error;
    }
}

// ===== Authentication APIs (Threedium OAuth) =====

export const authApi = {
    /**
     * Get the Threedium OAuth login URL
     * Frontend should redirect the user to this URL
     */
    getLoginUrl: async () => {
        const response = await cloudFetch('/api/auth/threedium/login-url');
        if (response?.state) {
            sessionStorage.setItem('oauth_state', response.state);
        }
        return response;
    },

    /**
     * Exchange user_app_id + otp for our JWT tokens (Threedium OAuth callback)
     * @param {string} userAppId - User app token from Threedium redirect
     * @param {string|null} otp - One-Time Password from Threedium redirect (null on older staging envs)
     */
    threediumCallback: async (userAppId, otp = null, state = null) => {
        const body = { user_app_id: userAppId };
        if (otp) body.otp = otp;
        const oauthState = state || sessionStorage.getItem('oauth_state');
        if (oauthState) body.state = oauthState;
        const response = await cloudFetch('/api/auth/threedium/callback', {
            method: 'POST',
            body: JSON.stringify(body),
        });
        sessionStorage.removeItem('oauth_state');
        if (response.access_token) {
            persistAuthTokens({
                accessToken: response.access_token,
                refreshToken: response.refresh_token,
            });
        }
        return response;
    },

    /**
     * Logout
     */
    logout: async (refreshToken) => {
        const token = refreshToken || getRefreshToken();
        try {
            if (token) {
                const headers = { 'Content-Type': 'application/json' };
                const accessToken = getStoredToken();
                if (accessToken) {
                    headers['Authorization'] = `Bearer ${accessToken}`;
                }
                await fetchWithTimeout(`${CLOUD_API_BASE}/api/auth/logout`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ refresh_token: token }),
                });
            }
        } catch {
            // Best-effort server-side token invalidation
        } finally {
            clearAuthTokens();
        }
    },

    /**
     * Get current user
     */
    me: () => cloudFetch('/api/user/me'),

    /**
     * Refresh token
     */
    refresh: (refreshToken) => cloudFetch('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
    }),

    /**
     * Check if user is authenticated
     */
    isAuthenticated: () => !!getAuthToken(),
};

// ===== User APIs =====

export const userApi = {
    /**
     * Get user profile
     */
    getProfile: () => cloudFetch('/api/user/me'),

    /**
     * Update user profile
     */
    updateProfile: (data) =>
        cloudFetch('/api/user/me', {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    /**
     * Get usage statistics (basic - for header/sidebar display)
     */
    getUsage: () => cloudFetch('/api/user/usage'),
};

// ===== Usage Analytics APIs =====

export const usageApi = {
    /**
     * Get comprehensive usage analytics
     * @param {Object} options - Query options
     * @param {string} options.period - 'daily' or 'monthly'
     * @param {string} options.startDate - ISO date string
     * @param {string} options.endDate - ISO date string
     * @param {string} options.modelId - Filter by model
     * @param {string} options.provider - Filter by provider
     */
    getAnalytics: (options = {}) => {
        const params = new URLSearchParams();
        if (options.period) params.append('period', options.period);
        if (options.startDate) params.append('start_date', options.startDate);
        if (options.endDate) params.append('end_date', options.endDate);
        if (options.modelId) params.append('model_id', options.modelId);
        if (options.provider) params.append('provider', options.provider);
        const queryString = params.toString();
        return cloudFetch(`/api/usage/analytics${queryString ? '?' + queryString : ''}`);
    },
};

// ===== Folder APIs (for organizing projects) =====

export const folderApi = {
    /**
     * List all folders
     */
    list: () => cloudFetch('/api/folders'),

    /**
     * Create folder
     * @param {string} name - Folder name
     * @param {string|null} parentId - Parent folder ID (optional)
     * @param {Object} options - Additional options { color, icon }
     */
    create: (name, parentId = null, options = {}) =>
        cloudFetch('/api/folders', {
            method: 'POST',
            body: JSON.stringify({ 
                name, 
                parent_id: parentId,
                color: options.color,
                icon: options.icon,
            }),
        }),

    /**
     * Update folder
     */
    update: (folderId, data) =>
        cloudFetch(`/api/folders/${folderId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    /**
     * Delete folder
     */
    delete: (folderId) =>
        cloudFetch(`/api/folders/${folderId}`, {
            method: 'DELETE',
        }),
};

// ===== Cloud Project APIs =====

export const cloudProjectApi = {
    /**
     * List cloud projects with search, pagination, and filtering
     * 
     * @param {Object} options - Query options
     * @param {string|null} options.folderId - Filter by folder ID
     * @param {string|null} options.search - Search query (searches name, display_name)
     * @param {number} options.page - Page number (1-indexed)
     * @param {number} options.pageSize - Items per page (max 100)
     * @param {string|null} options.status - Filter by status
     */
    list: ({ folderId = null, search = null, page = 1, pageSize = 50, status = null } = {}) => {
        const params = new URLSearchParams();
        if (folderId) params.append('folder_id', folderId);
        if (search) params.append('search', search);
        if (page) params.append('page', page.toString());
        if (pageSize) params.append('page_size', pageSize.toString());
        if (status) params.append('status', status);
        
        const queryString = params.toString();
        return cloudFetch(`/api/projects${queryString ? `?${queryString}` : ''}`);
    },

    /**
     * Get project details
     */
    get: (projectId) => cloudFetch(`/api/projects/${projectId}`),

    /**
     * Create cloud project (basic - without template)
     */
    create: (data) =>
        cloudFetch('/api/projects', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    /**
     * Create project from template (with git initialization)
     * This is the main endpoint for creating new projects.
     * 
     * @param {Object} data
     * @param {string} data.name - Project name
     * @param {string} data.template - Template: 'unlimited3d', 'threejs', 'r3f', 'gamekit'
     * @param {string} data.solution_id - Required for unlimited3d template
     * @param {string} data.description - Optional description
     * @param {string} data.folder_id - Optional folder UUID
     */
    createFromTemplate: (data) =>
        cloudFetch('/api/projects/from-template', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    /**
     * Update project
     */
    update: (projectId, data) =>
        cloudFetch(`/api/projects/${projectId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    /**
     * Delete project
     */
    delete: (projectId) =>
        cloudFetch(`/api/projects/${projectId}`, {
            method: 'DELETE',
        }),

    /**
     * Deploy project
     */
    deploy: (projectId) =>
        cloudFetch(`/api/projects/${projectId}/deploy`, {
            method: 'POST',
        }),

    /**
     * Get deployment status
     */
    getDeploymentStatus: (projectId) =>
        cloudFetch(`/api/projects/${projectId}/deployment`),

    /**
     * Get project deployments history
     */
    getDeployments: (projectId) =>
        cloudFetch(`/api/projects/${projectId}/deployments`),

    // ===== Publish APIs =====

    /**
     * Publish a project to make it publicly accessible
     * @param {string} projectId - Project ID
     * @param {Object} options - Publish options
     * @param {string} options.subdomain - Custom subdomain (auto-generated if not provided)
     * @param {string} options.visibility - 'PUBLIC' or 'PASSWORD'
     * @param {string} options.password - Required if visibility is 'PASSWORD'
     */
    publish: (projectId, options = {}) =>
        cloudFetch(`/api/publish/${projectId}`, {
            method: 'POST',
            body: JSON.stringify(options),
        }),

    /**
     * Get publish status for a project
     */
    getPublishStatus: (projectId) =>
        cloudFetch(`/api/publish/${projectId}/status`),

    /**
     * Update publish settings (subdomain, visibility, password)
     */
    updatePublishSettings: (projectId, settings) =>
        cloudFetch(`/api/publish/${projectId}/settings`, {
            method: 'PUT',
            body: JSON.stringify(settings),
        }),

    /**
     * Unpublish a project (remove from public access)
     */
    unpublish: (projectId) =>
        cloudFetch(`/api/publish/${projectId}`, {
            method: 'DELETE',
        }),

    /**
     * Check if a subdomain is available
     * @param {string} subdomain - Subdomain to check
     * @param {string} projectId - Optional project ID (for republishing - excludes own subdomain from "taken" check)
     */
    checkSubdomain: (subdomain, projectId = null) =>
        cloudFetch('/api/publish/check-subdomain', {
            method: 'POST',
            body: JSON.stringify({ 
                subdomain,
                project_id: projectId  // Allows republishing with same subdomain
            }),
        }),
    
    // ===== Git Operations =====
    
    /**
     * Push changes to cloud
     * @param {string} projectId - AWS project UUID
     * @param {Object} files - Dict of file paths to contents
     * @param {string} commitMessage - Commit message
     */
    pushChanges: (projectId, files, commitMessage = 'Agent changes') =>
        cloudFetch(`/api/projects/${projectId}/git/push`, {
            method: 'POST',
            body: JSON.stringify({ files, commit_message: commitMessage }),
        }),
    
    /**
     * Revert project to a previous commit (undo operation)
     * @param {string} projectId - AWS project UUID
     * @param {string} commitSha - Commit SHA to revert to
     * @param {string} messageId - Optional message ID (to delete messages after this)
     */
    revertToCommit: (projectId, commitSha, messageId = null) =>
        cloudFetch(`/api/projects/${projectId}/git/revert`, {
            method: 'POST',
            body: JSON.stringify({ commit_sha: commitSha, message_id: messageId }),
        }),
    
    /**
     * Get commit history
     * @param {string} projectId - AWS project UUID
     * @param {number} limit - Max commits to return
     */
    getCommits: (projectId, limit = 50) =>
        cloudFetch(`/api/projects/${projectId}/git/commits?limit=${limit}`),
    
    /**
     * Get project files at a specific commit
     * @param {string} projectId - AWS project UUID
     * @param {string} commitSha - Optional commit SHA (default: HEAD)
     */
    getFiles: (projectId, commitSha = null) => {
        const params = commitSha ? `?commit_sha=${commitSha}` : '';
        return cloudFetch(`/api/projects/${projectId}/git/files${params}`);
    },
    
    /**
     * Get git repository info
     * @param {string} projectId - AWS project UUID
     */
    getGitInfo: (projectId) =>
        cloudFetch(`/api/projects/${projectId}/git/info`),
};

// ===== Agent Configuration APIs =====

export const agentConfigApi = {
    /**
     * List all agent configurations (user's + system presets)
     * @param {Object} options - Query options
     * @param {boolean} options.includeSystem - Include system presets (default: true)
     * @param {boolean} options.systemOnly - Only return system presets (default: false)
     * @param {string} options.search - Search by name
     * @param {string} options.targetTemplate - Filter by project template (e.g. 'unlimited3d', 'threejs')
     */
    list: ({ includeSystem = true, systemOnly = false, search = '', targetTemplate = null } = {}) => {
        const params = new URLSearchParams();
        params.set('include_system', includeSystem);
        params.set('system_only', systemOnly);
        if (search) params.set('search', search);
        if (targetTemplate) params.set('target_template', targetTemplate);
        return cloudFetch(`/api/agent-configs?${params.toString()}`);
    },

    /**
     * Get default configuration (user's default or first system preset)
     * @param {string|null} targetTemplate - Filter by project template (e.g. 'r3f', 'threejs', 'unlimited3d')
     */
    getDefault: (targetTemplate = null) => {
        const params = new URLSearchParams();
        if (targetTemplate) params.set('target_template', targetTemplate);
        const qs = params.toString();
        return cloudFetch(`/api/agent-configs/default${qs ? `?${qs}` : ''}`);
    },

    /**
     * Get agent configuration by ID
     */
    get: (configId) => cloudFetch(`/api/agent-configs/${configId}`),

    /**
     * Create a new user configuration (fork from system or create new)
     */
    create: (config) =>
        cloudFetch('/api/agent-configs', {
            method: 'POST',
            body: JSON.stringify(config),
        }),

    /**
     * Update an existing user configuration
     */
    update: (configId, config) =>
        cloudFetch(`/api/agent-configs/${configId}`, {
            method: 'PUT',
            body: JSON.stringify(config),
        }),

    /**
     * Delete a user configuration
     */
    delete: (configId) =>
        cloudFetch(`/api/agent-configs/${configId}`, {
            method: 'DELETE',
        }),
};

// ===== Threedium (Unlimited3D) APIs =====

export const threediumApi = {
    /**
     * Get Threedium API keys status (auto-managed via OAuth)
     */
    getKeysStatus: () => cloudFetch('/api/threedium/keys'),

    /**
     * Get Threedium user profile info
     */
    getUserInfo: () => cloudFetch('/api/threedium/user/info'),

    /**
     * Get Threedium subscription details
     */
    getSubscription: () => cloudFetch('/api/threedium/user/subscription'),

    /**
     * Get Threedium user credits
     */
    getCredits: () => cloudFetch('/api/threedium/user/credits'),

    /**
     * List solutions (experiences) from Threedium
     * @param {Object} options - Search/pagination options
     * @param {string} options.search - Search term
     * @param {number} options.limit - Results per page (default 20)
     * @param {number} options.page - Page number (default 1)
     */
    listSolutions: (options = {}) => {
        const params = new URLSearchParams();
        if (options.search) params.append('search', options.search);
        if (options.limit) params.append('limit', options.limit);
        if (options.page) params.append('page', options.page);
        const queryString = params.toString();
        return cloudFetch(`/api/threedium/solutions${queryString ? '?' + queryString : ''}`);
    },

    /**
     * Get solution details
     * @param {number} solutionId - Solution ID
     */
    getSolution: (solutionId) => cloudFetch(`/api/threedium/solutions/${solutionId}`),

    /**
     * List Threedium projects (experience containers)
     * @param {Object} options - Search/pagination options
     */
    listProjects: (options = {}) => {
        const params = new URLSearchParams();
        if (options.search) params.append('search', options.search);
        if (options.limit) params.append('limit', options.limit);
        if (options.page) params.append('page', options.page);
        const queryString = params.toString();
        return cloudFetch(`/api/threedium/projects${queryString ? '?' + queryString : ''}`);
    },

    /**
     * List solutions within a specific Threedium project
     * @param {number} projectId - Project ID
     * @param {Object} options - Search/pagination options
     */
    listProjectSolutions: (projectId, options = {}) => {
        const params = new URLSearchParams();
        if (options.search) params.append('search', options.search);
        if (options.limit) params.append('limit', options.limit);
        if (options.page) params.append('page', options.page);
        const queryString = params.toString();
        return cloudFetch(`/api/threedium/projects/${projectId}/solutions${queryString ? '?' + queryString : ''}`);
    },
};

// ===== Knowledge Base APIs =====

export const knowledgeBaseApi = {
    /**
     * List all knowledge bases for current user
     * @param {Object} options - Query options
     * @param {string} options.search - Search by name
     * @param {boolean} options.includeInactive - Include inactive KBs
     */
    list: (options = {}) => {
        const params = new URLSearchParams();
        if (options.search) params.append('search', options.search);
        if (options.includeInactive) params.append('include_inactive', 'true');
        const queryString = params.toString();
        return cloudFetch(`/api/knowledge-bases${queryString ? '?' + queryString : ''}`);
    },

    /**
     * Get a specific knowledge base
     */
    get: (kbId) => cloudFetch(`/api/knowledge-bases/${kbId}`),

    /**
     * Create a new knowledge base
     */
    create: (data) =>
        cloudFetch('/api/knowledge-bases', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    /**
     * Update a knowledge base
     */
    update: (kbId, data) =>
        cloudFetch(`/api/knowledge-bases/${kbId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    /**
     * Delete a knowledge base
     */
    delete: (kbId) =>
        cloudFetch(`/api/knowledge-bases/${kbId}`, {
            method: 'DELETE',
        }),

    /**
     * Test a knowledge base configuration
     * @param {string} kbId - Knowledge base ID
     * @param {Object} data - Test data
     * @param {string} data.query - Test query
     * @param {number} data.top_k - Number of results
     */
    test: (kbId, data) =>
        cloudFetch(`/api/knowledge-bases/${kbId}/test`, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    /**
     * Get available KB tools for agents
     */
    getToolDefinitions: () => cloudFetch('/api/knowledge-bases/tools/available'),

    /**
     * Introspect a Pinecone index to auto-discover namespaces and metadata
     * @param {Object} data - Introspect request
     * @param {string} data.pinecone_index_name - Index name
     * @param {string} data.pinecone_host - Optional host URL for serverless
     */
    introspect: (data) =>
        cloudFetch('/api/knowledge-bases/introspect', {
            method: 'POST',
            body: JSON.stringify(data),
        }),
};

// ===== Session Backend APIs (ECS Fargate lifecycle) =====

export const sessionApi = {
    /**
     * Request a session backend (spawns or returns existing).
     * Only works when DEPLOYMENT_MODE=cloud on the AWS backend.
     * @param {Object} [options]
     * @param {boolean} [options.forceNew] - Tear down any existing session and start fresh
     */
    request: ({ forceNew = false } = {}) =>
        cloudFetch(`/api/sessions${forceNew ? '?force_new=true' : ''}`, { method: 'POST' }),

    /**
     * Send heartbeat to keep session alive
     */
    heartbeat: () => cloudFetch('/api/sessions/heartbeat', { method: 'POST' }),

    /**
     * End a session explicitly
     */
    end: (sessionId) => cloudFetch(`/api/sessions/${sessionId}`, { method: 'DELETE' }),

    /**
     * Get current user's active session (if any)
     */
    get: () => cloudFetch('/api/sessions'),
};

// ===== Health Check =====

export const healthApi = {
    /**
     * Check if cloud backend is available
     */
    check: async () => {
        try {
            const response = await fetch(`${CLOUD_API_BASE}/health`);
            return response.ok;
        } catch {
            return false;
        }
    },

    /**
     * Get backend status
     */
    status: () => cloudFetch('/health'),
};

export default {
    auth: authApi,
    user: userApi,
    usage: usageApi,
    folder: folderApi,
    cloudProject: cloudProjectApi,
    agentConfig: agentConfigApi,
    knowledgeBase: knowledgeBaseApi,
    threedium: threediumApi,
    session: sessionApi,
    health: healthApi,
    setAuthToken,
};

